import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Shield, Eye, Lock, Users } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex p-4 rounded-full bg-green-100 text-green-600 mb-4">
              <Shield className="h-8 w-8" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-gray-600">
              Last updated: August 2026
            </p>
          </div>

          {/* HIPAA Notice */}
          <Alert className="mb-8 border-blue-200 bg-blue-50">
            <Shield className="h-4 w-4" />
            <AlertDescription className="text-blue-800">
              <strong>Privacy Commitment:</strong> We take your privacy seriously and are committed to 
              protecting your personal information. We comply with applicable privacy laws and maintain 
              strict confidentiality standards.
            </AlertDescription>
          </Alert>

          <Card className="shadow-lg">
            <CardContent className="p-8 prose max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Personal Information You Provide</h3>
              <p className="text-gray-700 mb-4">
                When you use our services, we may collect:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-1">
                <li>Name and contact information (email, phone number)</li>
                <li>Location information (city, state, ZIP code)</li>
                <li>Treatment preferences and needs</li>
                <li>Insurance information (when provided)</li>
                <li>Communication preferences</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-3">Automatically Collected Information</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-1">
                <li>IP address and device information</li>
                <li>Browser type and version</li>
                <li>Pages visited and time spent on site</li>
                <li>Referral sources</li>
                <li>Search queries and interactions</li>
                <li>Information collected by the third-party analytics and advertising tools described in Section 4 (Cookies and Tracking)</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
                    <Users className="h-5 w-5 mr-2" />
                    Connecting You to Treatment
                  </h3>
                  <ul className="text-blue-800 text-sm space-y-1">
                    <li>• Match you with appropriate facilities</li>
                    <li>• Provide referrals and recommendations</li>
                    <li>• Facilitate communication with providers</li>
                    <li>• Follow up on your treatment search</li>
                  </ul>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-green-900 mb-2 flex items-center">
                    <Eye className="h-5 w-5 mr-2" />
                    Improving Our Services
                  </h3>
                  <ul className="text-green-800 text-sm space-y-1">
                    <li>• Analyze website usage and trends</li>
                    <li>• Improve user experience</li>
                    <li>• Develop new features</li>
                    <li>• Monitor service performance</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Information Sharing</h2>
              
              <div className="bg-yellow-50 p-4 rounded-lg mb-6">
                <h3 className="font-semibold text-yellow-900 mb-2">With Treatment Facilities</h3>
                <p className="text-yellow-800 text-sm">
                  We share your contact information and treatment preferences with facilities you're
                  interested in or that match your needs. This enables them to contact you directly
                  about their services.
                </p>
              </div>

              <div className="bg-yellow-50 p-4 rounded-lg mb-6">
                <h3 className="font-semibold text-yellow-900 mb-2">With Analytics and Advertising Partners</h3>
                <p className="text-yellow-800 text-sm">
                  We use third-party tools - currently Meta (Facebook) Pixel and Microsoft Clarity,
                  with Google Analytics/Google Tag planned - that automatically collect information
                  about your visit and share it with those providers. See Section 4 (Cookies and
                  Tracking) for what each tool collects and how to opt out.
                </p>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mb-3">We DO NOT Share Information With:</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-1">
                <li>Third-party marketers (except treatment facilities and the analytics/advertising partners described above)</li>
                <li>Data brokers or list companies</li>
                <li>Unauthorized parties</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-3">Legal Requirements</h3>
              <p className="text-gray-700 mb-6">
                We may disclose information when required by law, court order, or to protect our rights, 
                safety, or the safety of others.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Cookies and Tracking</h2>
              <p className="text-gray-700 mb-4">
                We use cookies and similar technologies to remember your preferences, analyze
                website traffic and usage patterns, and improve website functionality. This
                includes the following third-party tools:
              </p>

              <div className="space-y-4 mb-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-gray-900">Meta (Facebook) Pixel</h4>
                    <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Active</span>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Tracks page visits on our site and shares this information with Meta to measure
                    and improve our advertising and to show relevant ads to visitors on Facebook and
                    Instagram. Governed by Meta's own privacy policy.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-gray-900">Microsoft Clarity</h4>
                    <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Active</span>
                  </div>
                  <p className="text-gray-700 text-sm">
                    Records how visitors interact with our site - clicks, scrolling, and mouse
                    movement - to help us understand and improve the user experience. Governed by
                    Microsoft's own privacy policy.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-gray-900">Google Analytics / Google Tag</h4>
                    <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">Planned, not yet active</span>
                  </div>
                  <p className="text-gray-700 text-sm">
                    We plan to add Google's analytics tools to measure website traffic and usage.
                    This entry will be updated once it goes live. Once active, it will be governed
                    by Google's own privacy policy.
                  </p>
                </div>
              </div>

              <p className="text-gray-700 mb-6">
                You can opt out of Meta's interest-based advertising through your{' '}
                <a href="https://www.facebook.com/settings?tab=ads" className="text-blue-600 hover:underline">
                  Facebook ad preferences
                </a>. You can control cookies generally through your browser settings, but some
                features may not work properly if cookies are disabled.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Security</h2>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <Lock className="h-5 w-5 mr-2" />
                  Security Measures
                </h3>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• SSL encryption for data transmission</li>
                  <li>• Secure servers and databases</li>
                  <li>• Regular security audits and updates</li>
                  <li>• Limited access to personal information</li>
                  <li>• Staff training on privacy practices</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights and Choices</h2>
              
              <h3 className="text-lg font-semibold text-gray-800 mb-3">You Have the Right To:</h3>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-1">
                <li>Access the personal information we have about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Opt out of certain communications</li>
                <li>Request information about how we use your data</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mb-3">Communication Preferences</h3>
              <p className="text-gray-700 mb-6">
                You can opt out of non-essential communications at any time by:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-6 space-y-1">
                <li>Clicking unsubscribe links in emails</li>
                <li>Contacting us directly</li>
                <li>Updating your preferences on our website</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Children's Privacy</h2>
              <p className="text-gray-700 mb-6">
                Our services are not intended for children under 13. We do not knowingly collect 
                personal information from children under 13. If we become aware that we have 
                collected such information, we will delete it promptly.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. California Privacy Rights</h2>
              <p className="text-gray-700 mb-6">
                California residents have additional rights under the California Consumer Privacy Act (CCPA), 
                including the right to know what personal information is collected, sold, or disclosed, 
                and the right to delete personal information.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Data Retention</h2>
              <p className="text-gray-700 mb-6">
                We retain your information for as long as necessary to provide our services and 
                comply with legal obligations. Contact information may be retained to facilitate 
                ongoing treatment connections and follow-up services.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to This Policy</h2>
              <p className="text-gray-700 mb-6">
                We may update this Privacy Policy from time to time. We will notify you of any 
                material changes by posting the new policy on our website and updating the 
                "Last updated" date.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have questions about this Privacy Policy or want to exercise your privacy rights:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>Privacy Officer</strong><br />
                  Addiction Rehab Call<br />
                  Email: privacy@addictionrehabcall.com<br />
                  Phone: 1-888-968-1014<br />
                  Website: addictionrehabcall.com/contact
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Link 
              to="/" 
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;